import React from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalOverlay } from '@chakra-ui/react';
import { AxiosError } from 'axios';

import { CategoryQuantity, RelevoInventoryResponse } from '../../../../../api/types';
import { ProcessSpinner } from '../../../../../common/components/ProcessSpinner/ProcessSpinner';
import { HttpException } from '../../../../../common/models/httpException';
import { useTranslations } from '../../../../../contexts/LocalizationContext';
import { useLocalizationContext } from '../../../../../contexts/LocalizationContext/useLocalizationContext.hook';
import { useScannedRelevosContext } from '../../../../../contexts/ScannedRelevosContext';
import { useAssignUnknownItems } from '../../../../../services/relevos';
import { AssignItemsConfirmFooter } from '../../AssignItemsConfirmFooter/AssignItemsConfirmFooter';
import { AssignItemsResultStatus, AssignItemsSuccessBody } from '../../AssignItemsSuccessBody/AssignItemsSuccessBody';
import { buildUnknownDeliveryNoteContext } from '../../DeliveryNote/deliveryNoteUtils';
import { downloadDeliveryNotePdf } from '../../DeliveryNote/downloadDeliveryNotePdf';
import { UnknownItemsConfirmBody } from './UnknownItemsConfirmBody';

type AssignUnknownItemsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  categoriesToCreate: CategoryQuantity;
  inventory?: RelevoInventoryResponse;
};
export const AssignUnknownItemsModal = ({
  isOpen,
  onClose,
  categoriesToCreate,
  inventory,
}: AssignUnknownItemsModalProps) => {
  const translations = useTranslations();
  const { language } = useLocalizationContext();
  const {
    mutateAsync: assignUnknownItems,
    isLoading: isAssigningUnknownItems,
    data: unknownItemsAssignedData,
  } = useAssignUnknownItems();
  const { restaurantId, restaurant, applicationUser, quickContact, isDeliveryNoteEnabled } = useScannedRelevosContext();

  const executeUnknownAssign = async () => {
    if (restaurantId && (applicationUser || quickContact)) {
      const userId = applicationUser?.id || quickContact?.id || '';
      try {
        const response = await assignUnknownItems({ userId, request: categoriesToCreate, restaurantId });

        if (quickContact && isDeliveryNoteEnabled && response) {
          const deliveryNoteContext = buildUnknownDeliveryNoteContext({
            assignedCategories: response.assignedCategories || {},
            quickContactName: quickContact.quickContactName,
            quickContactAddress: quickContact.address,
            restaurantName: restaurant?.restaurantName || null,
            restaurantAddress: restaurant?.address || null,
            orderNumber: response.orderNumber,
            language,
            inventory,
          });

          if (deliveryNoteContext) {
            await downloadDeliveryNotePdf({
              context: deliveryNoteContext,
              translations,
            });
          }
        }
      } catch (error) {
        const axiosError = error as AxiosError<HttpException>;
        toast.error(axiosError.response?.data.message || translations('something_went_wrong'));
      }
    }
  };

  const assignedItemsCount = Object.values(unknownItemsAssignedData?.assignedCategories ?? {}).reduce(
    (s, v) => (Number.isFinite(+v) ? s + +v : s),
    0,
  );
  const notAssignedItemsCount = Object.values(unknownItemsAssignedData?.notAssignedCategories ?? {}).reduce(
    (s, v) => (Number.isFinite(+v) ? s + +v : s),
    0,
  );
  const totalAttemptedItems = unknownItemsAssignedData ? assignedItemsCount + notAssignedItemsCount : 0;

  let assignResultStatus: AssignItemsResultStatus | undefined = undefined;
  if (unknownItemsAssignedData) {
    if (notAssignedItemsCount && assignedItemsCount) {
      assignResultStatus = 'partial';
    } else if (notAssignedItemsCount && !assignedItemsCount) {
      assignResultStatus = 'failure';
    } else {
      assignResultStatus = 'success';
    }
  }

  const assignmentUserLabel =
    quickContact?.quickContactName || applicationUser?.userId || quickContact?.id || applicationUser?.id || '';

  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered size={'3xl'} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent p={2}>
        {!isAssigningUnknownItems && !unknownItemsAssignedData ? (
          <>
            <ModalCloseButton />
            <ModalBody p={8}>
              <UnknownItemsConfirmBody categoriesToCreate={categoriesToCreate} inventory={inventory} />
            </ModalBody>
            <ModalFooter>
              <AssignItemsConfirmFooter
                onConfirm={executeUnknownAssign}
                onCancel={onClose}
                isLoading={isAssigningUnknownItems}
              />
            </ModalFooter>
          </>
        ) : null}
        {isAssigningUnknownItems ? (
          <ModalBody p={2}>
            <ProcessSpinner />
          </ModalBody>
        ) : null}
        {unknownItemsAssignedData ? (
          <>
            <ModalCloseButton />
            <ModalBody>
              <AssignItemsSuccessBody
                status={assignResultStatus}
                assignedCount={assignedItemsCount}
                totalCount={totalAttemptedItems}
                userLabel={assignmentUserLabel}
                orderNumber={unknownItemsAssignedData.orderNumber ?? null}
                showProgress={assignResultStatus === 'success'}
                onAnimationComplete={onClose}
              />
            </ModalBody>
          </>
        ) : null}
      </ModalContent>
    </Modal>
  );
};
